import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import BackButton from '../../components/common/BackButton';
import applicationApi from '../../services/api/application.api';

interface ApiKey {
  id: string;
  name: string;
  api_key: string;
  public_api_key?: string;
  environment: string;
  language?: string;
  framework?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export default function APIKeysPage() {
  const { t } = useTranslation(['settings', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState<ApiKey | null>(null);
  const [newKeyCreated, setNewKeyCreated] = useState<ApiKey | null>(null);
  const [newKey, setNewKey] = useState({
    name: '',
    description: '',
    language: 'javascript',
    framework: '',
    environment: 'production' as 'development' | 'staging' | 'production',
  });

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await applicationApi.getAll();
      setApiKeys(Array.isArray(response.data) ? response.data : []);
    } catch (err: any) {
      setError(err.message || 'Failed to load API keys');
      console.error('API keys fetch error:', err);
      setApiKeys([]);
    } finally {
      setLoading(false);
    }
  };

  const getEnvironmentColor = (environment: string) => {
    switch (environment) {
      case 'production':
        return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
      case 'staging':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'development':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    return status === 'active'
      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
  };

  const handleCreate = async () => {
    if (!newKey.name) {
      toast.error('Please enter a name', {
        duration: 3000,
        icon: '⚠️',
      });
      return;
    }

    try {
      const response = await applicationApi.create(newKey);
      setNewKeyCreated(response.data);
      setShowCreateModal(false);
      setNewKey({ name: '', description: '', language: 'javascript', framework: '', environment: 'production' });
      fetchApiKeys();
      // Show the newly created key
      setTimeout(() => {
        setSelectedKey(response.data);
        setShowKeyModal(true);
      }, 100);
      toast.success('API key created successfully!', {
        duration: 3000,
        icon: '✅',
      });
    } catch (err: any) {
      toast.error('Failed to create API key: ' + (err.message || 'Unknown error'), {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const copyApiKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success('API key copied to clipboard!', {
      duration: 2000,
      icon: '📋',
    });
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) return;

    try {
      await applicationApi.update(id, { status: 'inactive' });
      fetchApiKeys();
      toast.success('API key revoked successfully', {
        duration: 3000,
        icon: '✅',
      });
    } catch (err: any) {
      toast.error('Failed to revoke API key: ' + (err.message || 'Unknown error'), {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleRegenerate = async (id: string) => {
    if (!confirm('Are you sure you want to regenerate this API key? The old key will stop working immediately.')) return;

    try {
      const response = await applicationApi.regenerateApiKey(id);
      setSelectedKey(response.data);
      setShowKeyModal(true);
      fetchApiKeys();
      toast.success('API key regenerated successfully!', {
        duration: 3000,
        icon: '✅',
      });
    } catch (err: any) {
      toast.error('Failed to regenerate API key: ' + (err.message || 'Unknown error'), {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 dark:text-gray-400">Loading API keys...</div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout>
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-red-800 dark:text-red-400">Error: {error}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <BackButton />
      <div className="mb-8 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{t('settings:apiKeys.title')}</h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">{t('settings:apiKeys.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          <svg className="mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          {t('settings:apiKeys.createKey')}
        </button>
      </div>

      {/* Stats */}
      <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('common:labels.total')}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">{apiKeys.length}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">{t('common:status.active')}</p>
          <p className="mt-2 text-3xl font-bold text-green-600 dark:text-green-400">
            {apiKeys.filter((k) => k.status === 'active').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">Production</p>
          <p className="mt-2 text-3xl font-bold text-red-600 dark:text-red-400">
            {apiKeys.filter((k) => k.environment === 'production').length}
          </p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-sm text-gray-600 dark:text-gray-400">Development</p>
          <p className="mt-2 text-3xl font-bold text-blue-600 dark:text-blue-400">
            {apiKeys.filter((k) => k.environment === 'development').length}
          </p>
        </div>
      </div>

      {/* Warning Banner */}
      <div className="mb-8 rounded-xl border border-yellow-200 bg-yellow-50 p-4 dark:border-yellow-800 dark:bg-yellow-900/10">
        <div className="flex items-start">
          <svg className="mr-3 h-5 w-5 text-yellow-600 dark:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <p className="font-medium text-yellow-800 dark:text-yellow-400">Keep your API keys secure</p>
            <p className="mt-1 text-sm text-yellow-700 dark:text-yellow-500">
              API keys provide access to your account. Never share them in publicly accessible areas or commit them to source
              control.
            </p>
          </div>
        </div>
      </div>

      {/* API Keys Table */}
      {apiKeys.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <p className="text-gray-600 dark:text-gray-400">No API keys found. Create one to get started.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
          <table className="w-full">
            <thead className="border-b border-gray-200 dark:border-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('settings:apiKeys.name')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  App ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('settings:apiKeys.key')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  Environment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('common:labels.status')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase text-gray-500 dark:text-gray-400">
                  {t('common:labels.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
              {apiKeys.map((apiKey) => (
                <tr key={apiKey.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="whitespace-nowrap px-6 py-4">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{apiKey.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-500">
                        Created {new Date(apiKey.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <code className="font-mono text-xs text-indigo-600 dark:text-indigo-400">{apiKey.id.substring(0, 8)}...</code>
                      <button
                        onClick={() => { navigator.clipboard.writeText(apiKey.id); toast.success(t('settings:applications.appIdCopied', 'App ID copied!')); }}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        title={t('settings:applications.copyAppId', 'Copy App ID')}
                      >
                        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2">
                        <code className="font-mono text-xs text-gray-600 dark:text-gray-400">{apiKey.api_key?.substring(0, 20)}...</code>
                        <button
                          onClick={() => {
                            setSelectedKey(apiKey);
                            setShowKeyModal(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        >
                          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          </svg>
                        </button>
                      </div>
                      {apiKey.public_api_key && (
                        <code className="font-mono text-xs text-green-600 dark:text-green-400">{apiKey.public_api_key.substring(0, 24)}...</code>
                      )}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getEnvironmentColor(apiKey.environment)}`}>
                      {apiKey.environment}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusColor(apiKey.status)}`}>
                      {t(`common:status.${apiKey.status}`)}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleRegenerate(apiKey.id)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                      >
                        Regenerate
                      </button>
                      <button
                        onClick={() => handleRevoke(apiKey.id)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                      >
                        {t('settings:apiKeys.revoke')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">{t('settings:apiKeys.create.title')}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:apiKeys.create.name')}
                </label>
                <input
                  type="text"
                  value={newKey.name}
                  onChange={(e) => setNewKey({ ...newKey, name: e.target.value })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder="Production API Key"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t('settings:apiKeys.create.description')}
                </label>
                <textarea
                  value={newKey.description}
                  onChange={(e) => setNewKey({ ...newKey, description: e.target.value })}
                  rows={2}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder="Describe the purpose of this key..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Environment
                </label>
                <select
                  value={newKey.environment}
                  onChange={(e) => setNewKey({ ...newKey, environment: e.target.value as any })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                >
                  <option value="development">Development</option>
                  <option value="staging">Staging</option>
                  <option value="production">Production</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Language
                </label>
                <input
                  type="text"
                  value={newKey.language}
                  onChange={(e) => setNewKey({ ...newKey, language: e.target.value })}
                  className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  placeholder="javascript, python, go, etc."
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleCreate}
                  disabled={!newKey.name}
                  className="flex-1 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  {t('settings:apiKeys.create.submit')}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                >
                  {t('common:buttons.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Key Modal */}
      {showKeyModal && selectedKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-lg dark:border-gray-800 dark:bg-gray-900">
            <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">API Key - {selectedKey.name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-indigo-700 dark:text-indigo-300">{t('settings:applications.appId', 'App ID')}</label>
                <div className="mt-2 flex items-center space-x-2">
                  <code className="flex-1 overflow-x-auto rounded-lg bg-indigo-50 p-3 font-mono text-xs text-indigo-900 dark:bg-indigo-900/20 dark:text-indigo-100">
                    {selectedKey.id}
                  </code>
                  <button
                    onClick={() => copyApiKey(selectedKey.id)}
                    className="rounded-lg bg-indigo-600 p-3 text-white hover:bg-indigo-700"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-500">{t('settings:applications.appIdHelp', 'Use this ID to configure the SDK in your landing pages')}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">API Key (Private)</label>
                <div className="mt-2 flex items-center space-x-2">
                  <code className="flex-1 overflow-x-auto rounded-lg bg-gray-100 p-3 font-mono text-xs text-gray-900 dark:bg-gray-800 dark:text-white">
                    {selectedKey.api_key}
                  </code>
                  <button
                    onClick={() => copyApiKey(selectedKey.api_key)}
                    className="rounded-lg bg-indigo-600 p-3 text-white hover:bg-indigo-700"
                  >
                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {selectedKey.public_api_key && (
                <div>
                  <label className="block text-sm font-medium text-green-700 dark:text-green-300">Public API Key (Safe for frontend)</label>
                  <div className="mt-2 flex items-center space-x-2">
                    <code className="flex-1 overflow-x-auto rounded-lg bg-green-50 p-3 font-mono text-xs text-green-900 dark:bg-green-900/20 dark:text-green-100">
                      {selectedKey.public_api_key}
                    </code>
                    <button
                      onClick={() => copyApiKey(selectedKey.public_api_key!)}
                      className="rounded-lg bg-green-600 p-3 text-white hover:bg-green-700"
                    >
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              <div className="rounded-lg bg-yellow-50 p-4 dark:bg-yellow-900/10">
                <p className="text-sm text-yellow-800 dark:text-yellow-400">
                  ⚠️ Keep this API key secure. Anyone with this key can access your monitoring data.
                </p>
              </div>
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-gray-500 dark:text-gray-500">Environment:</span>{' '}
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getEnvironmentColor(selectedKey.environment)}`}>
                    {selectedKey.environment}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-gray-500 dark:text-gray-500">Created:</span>{' '}
                  <span className="text-gray-900 dark:text-white">{new Date(selectedKey.created_at).toLocaleString()}</span>
                </div>
                {selectedKey.language && (
                  <div className="text-sm">
                    <span className="text-gray-500 dark:text-gray-500">Language:</span>{' '}
                    <span className="text-gray-900 dark:text-white">{selectedKey.language}</span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowKeyModal(false)}
                className="w-full rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                {t('common:buttons.close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
}
